import i18n from "../i18n";
// constants
import {autoStartTimeFrame, ABSOLUTE} from "../constants/sensors-constants";
import {LASTUPDATED} from "../constants/general-constants";
import {numToStateName} from "../constants/map-constants";

/**
 * Private helper function to custom sort indexes,
 * extracted from name. Handles the case when
 * no index is present in the string.
 * @param keysArr
 * @returns {*}
 */
const _customSortKeys = (keysArr) => {
    const sortedKeysHelper = keysArr.sort((nameA, nameB) => {

        let indexA;
        let indexB;

        try {
            indexA = +nameA.match(/\d/g).join('');
        } catch (e) {
            indexA = 0;
        }

        try {
            indexB = +nameB.match(/\d/g).join('');
        } catch (e) {
            indexB = 0;
        }

        return indexA - indexB;
    });

    return sortedKeysHelper;
}
/**
 * Utility to get all related sensors (i.e. get Piezometers from sensors obj)
 * @param {String} name
 * @param {Object} data
 * @returns {[{},string[]]}
 */
export const getMeters = (name, data) => {
    if (!data || typeof data === 'undefined') {
        return [{}, []];
    }

    let filteredMeters = {};
    for (let key of Object.keys(data)) {
        if (key.toLowerCase().includes(name.toLowerCase()) && !key.toLowerCase().includes(ABSOLUTE) && data[key]!==null) {
            filteredMeters[key] = data[key];
        }
    }

    const sortedKeysHelper = _customSortKeys(Object.keys(filteredMeters));

    return [filteredMeters, sortedKeysHelper];
};

/**
 * Get sensor keys, clear, create set (uniques only) and return.
 * Used to determine correct sensor names for site.
 * @param {Object} sensors: map of sensors and values
 * @return {Array} spread set of unique keys
 *
 */
export const getSiteUniqueSensorsHelper = (sensors) => {
    // 1. Get keys and clear the not deeded lastUpdated.
    let keys = Object.keys(sensors);
    let indexOfLastUpdated = keys.indexOf(LASTUPDATED);

    keys.splice(indexOfLastUpdated, 1);

    // 2. Clear keys and get only unique ones:
    let uniqueKeys = new Set();
    for (let key of keys) {
        let clearedKey = key.replace(/\d+/g, '');
        uniqueKeys.add(clearedKey);
    }

    return [...uniqueKeys];
};

/**
 * Formats a Date object or returns formatted now (visualization only)
 * @param {Date} date - optional
 * @returns {string}  Ex. 2020-9-10 17:17
 */
export const formatDate = (date= new Date()) => {
    const dateStr = formatShortDate(date);
    const timeStr = formatShortTime(date);
    return `${dateStr} ${timeStr}`;
};

/**
 * Formats a Date object to ISO, but date only
 * @param {Date} date - optional
 * @returns {string}  Ex. 2020-09-20
 */
export const formatShortDate = (date= new Date()) => {

    const day = date.getDate();
    const month = date.getMonth() + 1;

    let formattedDate = date.getFullYear() + "-";
    formattedDate += (month >= 10 ? month : "0" + month) + "-";
    formattedDate += day >= 10 ? day : "0" + day;

    return formattedDate;
};

/**
 * Formats a Date object to ISO, but date only
 * @param {Date} date - optional
 * @returns {string}  Ex. 03/12/2021
 */
export const formatLabelDate = (date= new Date()) => {

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    let formatLabelDateVar = (day >= 10 ? day : "0" + day) + "/";
    formatLabelDateVar += (month >= 10 ? month : "0" + month) + "/";
    formatLabelDateVar += year;

    return formatLabelDateVar;
};

/**
 * Formats a Date object to ISO, but time only
 * @param {Date} date - optional
 * @returns {string} Ex. 17:17
 */
export const formatShortTime = (date = new Date()) => {

    const hours = date.getHours();
    const minutes = date.getMinutes();

    return `${hours >= 10 ? hours : "0" + hours}:${minutes >= 10 ? minutes : "0" + minutes}`;
};

/**
 * Formats a Date object time: hh:mm, Date dd.mm.yyyy
 * @param {Date} date - optional
 * @returns {Array} []  [hh:mm, dd.mm.yyyy]
 */
export const formatShortDateCard = (date= new Date()) => {
    const formattedHour = date.getHours() < 10 ? `0${date.getHours()}` : `${date.getHours()}`;
    const formattedMinutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : `${date.getMinutes()}`;

    return [
        `${formattedHour}:${formattedMinutes}`,
        `${date.toLocaleDateString()}`
    ]
};

/**
 * Translates the status from a digit to string,
 * returns the state as digit, as text and as class.
 * @param {number} stateAsDigit
 * @returns {[number, string, string]}
 */
export const translateStatus = (stateAsDigit) => {
    let stateAsText = '';
    let stateAsClass = 'danger';

    switch (stateAsDigit) {
        case 1:
            stateAsText = i18n.t('dashboard.normal');
            stateAsClass = 'normal';
            break;
        case 2:
            stateAsText = i18n.t('dashboard.warning');
            stateAsClass = 'warning';
            break;
        case 3:
            stateAsText = i18n.t('dashboard.not-monitored');
            stateAsClass = 'not-monitored';
            break;
        case 0:
            stateAsText = i18n.t('dashboard.danger');
            stateAsClass = 'danger';
            break;
    }
    return [stateAsDigit, stateAsText, stateAsClass];
};

/**
 * Gets a dynamic key by converting
 * a date timestamp to a number.
 */
export const getKey = () => {
    // use date as timestamp
    return + new Date();
};

/**
 * Convert a given date as string
 * to timestamp (number).
 */
export const getTimestamp = (dateStr) => {
    const theDate = new Date(dateStr);
    return +theDate;
};

/**
 * Simple function to NOT reliably generate a date X months back from now
 * Note: we don't need absolute accuracy.
 * @param {number} months: How many months back.
 * @returns {Date}
 */
export const getPastDate = (months = autoStartTimeFrame) => {
    const date = setHoursHelper();
    date.setMonth((date.getMonth() - months));
    return date;
};

/**
 * Generates a list of dates, each is one month in the past.
 * @param months
 * @returns {string[]}
 */
export const generateAutoMultiDates = (months = autoStartTimeFrame) => {
    const multiDates = [];

    // for the amount of months, generate one date for each
    // NOTE: index is months - 1 as we need the current date
    let index = months - 1;
    while (index > -1) {
        const pastDate = getPastDate(index);
        const pastDateStr = formatShortDate(pastDate);
        multiDates.push({
            date: pastDateStr,
            hasSeries: true
        });
        index--;
    }

    return multiDates;
};

/**
 * The viewer.min.js script adds a html object
 * for the tooltip at the end of the body, so
 * it is not managed by Vue and we need
 * to force remove it.
 */
export const removeMxTooltips = () => {
    // remove mxTooltips that still hang around
    const mxTooltips = document.querySelectorAll('.mxTooltip');
    for (let tooltipMx of mxTooltips) {
        tooltipMx.remove();
    }
};

/**
 * Function which removes Z parameter from date-time string and returns
 * new Date object without localization
 * @param {string} dateString
 * @returns {Date}
 */
export const parsedDate = (dateString) => {
    const slicedDate = dateString.includes('Z') ? dateString.slice(0, -1) : dateString;
    return new Date(slicedDate);
};

/**
 * Returns a new date with specific hours set.
 * @param {number} hours
 * @param {number} minutes
 * @param {number} seconds
 * @param {number} milliseconds
 * @returns {Date}
 */
export const setHoursHelper = (hours = 0, minutes = 0, seconds = 0, milliseconds = 0) => {
    return new Date(new Date().setHours(hours, minutes, seconds, milliseconds))
};

/**
 * Should sum the items in
 * a given state as to provide int overview.
 * @param {Object} psStates: Map of all dams with status as number (0-3)
 */
export const groupStatesByType = (psStates) => {
    let groupedStatesInt = {
        0: 0,
        1: 0,
        2: 0,
        3: 0
    };

    // now add the value to each group to receive total amount per group
    for (const key in psStates) {
        groupedStatesInt[psStates[key]]++;
    }

    return groupedStatesInt;
};

/**
 * Try to get a locale key based on numeric state passed
 * and use it to fetch the actual locale from json.
 * @param numState
 * @returns {string}
 */
export const convertNumStateToName = (numState) => {
    let localizedStateName = '';
    const localeKey = numToStateName?.[numState];

    if (localeKey && i18n.te(localeKey)) {
        localizedStateName = i18n.t(localeKey);
    }
    return localizedStateName;
};

/**
 *
 * @param {[[]]} psData: array of arrays (dams)
 * @param {string} filterStr: substring from search bar to serve as filter
 */
export const filterPsDataHelper = (psData, filterStr) => {

    if (psData?.['PS_stations'] && filterStr !== '') {
        const loweredFilter = filterStr.toLowerCase();

        const filtered = psData['PS_stations'].filter(item => {
            const lowerItemNameLatin = item[0].toLowerCase();
            const lowerItemNameCyrillic = item[6].toLowerCase();

            return lowerItemNameLatin.includes(loweredFilter) || lowerItemNameCyrillic.includes(loweredFilter);
        });

        return {
            ...psData,
            PS_stations: filtered
        }
    }
    return psData;
};

/**
 * Adds one day to the end as to improve search range at BE.
 * @param {Date} initDate
 * @param {number} days
 * @returns {Date}
 */
export const addOneDay = (initDate, days= 1) => {
    const newDate = setHoursHelper(24);
    newDate.setDate((initDate.getDate() + days));
    newDate.setMonth((initDate.getMonth()));
    return newDate;
};

/**
 * Check if two element collide on the screen.
 * Used to check if an InfoWindow is behind the legend
 * or the event panel.
 * NOTE:
 * The element is an object, received by calling element.getBoundingClientRect();
 * Credit to: Buu Nguyen
 * @param {Object} element1
 * @param {Object} element2
 * @returns {boolean} If elements collide visually
 */
export const checkIfElementsCollide = (element1, element2) => {
    return !(element1.right < element2.left ||
             element1.left > element2.right ||
             element1.bottom < element2.top ||
             element1.top > element2.bottom);
};

const getTranslation = (key) => {
    if (i18n.te(key)) {
        return i18n.t(key);
    } else if (i18n.te(`logs.${key}`)) {
        return i18n.t(`logs.${key}`);
    } else if (i18n.te(`sensors.${key}`)) {
        return i18n.t(`sensors.${key}`);
    }
    return key;
}

/**
 * Converts a given value to a string.
 * @param {*} fields
 * @returns {string} compiled string
 */
const convertFieldsToString = (fields) => {
    if (typeof fields === 'undefined') {
        return '';
    } else if (typeof  fields === "object") {
        return fields.join(", ");
    }
    return fields;
}

/**
 * Retrofit of the BE model implementation for
 * compiling the change message.
 * If needed, translate.
 * @param {Object} logMessageItem
 * @returns {string} The compiled message
 */
export const compileAndTranslateChangeMessage = (logMessageItem) => {
    const changeMessage = logMessageItem?.["json_change_message"] || [];
    try {
        let messages = [];
        for (const subMessage of changeMessage) {
            if ('added' in subMessage) {
                if (subMessage?.added && Object.entries(subMessage?.added) > 0) {
                    const name = getTranslation(convertFieldsToString(subMessage.added?.name));
                    const obj = convertFieldsToString(subMessage.added?.object);
                    messages.push(
                        `${getTranslation('logs.Added')} ${name} ${obj}.`
                    )
                }
                else {
                    messages.push(`${getTranslation('logs.Added')}.`);
                }
            }
            else if ('changed' in subMessage) {
                if ('name' in subMessage.changed) {
                    const name = getTranslation(convertFieldsToString(subMessage.changed?.name));
                    const fields = convertFieldsToString(subMessage.changed?.fields);
                    messages.push(
                        `${getTranslation('logs.Changed')} ${fields} ${name}.`
                    )
                }
                else {
                    const fields = convertFieldsToString(subMessage.changed?.fields);
                    messages.push(`${getTranslation('logs.Changed')} ${fields}.`);
                }
            }
            else if ('deleted' in subMessage) {
                const name = getTranslation(convertFieldsToString(subMessage.deleted?.name));
                messages.push(`${getTranslation('logs.Deleted')} ${name}.`)
            }
        }

        return messages.join(' ') || i18n.t('logs.no-fields');
    }
    catch(e) {
        return changeMessage || i18n.t('logs.error');
    }
}


